import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ToastMessage = {
  message?: string;
  type?: string;
  persistent?: boolean;
  action?: unknown;
  id?: number;
  timestamp?: number;
};

type ToastStateValue = {
  /** @deprecated Prefer queue via `toasterMessages` + `ToasterContainer`. Kept for rare readers. */
  toasterMessage: ToastMessage | null;
  toasterMessages: ToastMessage[];
};

type ToastDispatchValue = {
  setToasterMessage: (
    next:
      | ToastMessage
      | null
      | ((prev: ToastMessage | null) => ToastMessage | null)
  ) => void;
  closeToaster: () => void;
  addToast: (message: ToastMessage) => number;
  removeToast: (id: number) => void;
};

const ToastStateContext = createContext<ToastStateValue | null>(null);
const ToastDispatchContext = createContext<ToastDispatchValue | null>(null);

/**
 * UI chrome provider (toaster first). Split state/dispatch so toast updates do not
 * re-render DataProvider or unrelated `useData()` consumers.
 *
 * Delivery SoT is the `toasterMessages` queue rendered by `ToasterContainer`.
 * Legacy `setToasterMessage` / `addToast` only enqueue — they do **not** also set
 * `toasterMessage`, so page-level `<Toaster message={toasterMessage} />` does not
 * duplicate the global container.
 */
export function UiShellProvider({ children }: { children: ReactNode }) {
  const [toasterMessages, setToasterMessages] = useState<ToastMessage[]>([]);
  const [toasterMessage, setToasterMessage] = useState<ToastMessage | null>(null);

  const closeToaster = useCallback(() => {
    setToasterMessage(null);
    setToasterMessages([]);
  }, []);

  const addToast = useCallback((message: ToastMessage) => {
    const id = Date.now() + Math.random();
    const toast: ToastMessage = {
      id,
      message: message.message,
      type: message.type,
      persistent: message.persistent || false,
      action: message.action || null,
      timestamp: Date.now(),
    };

    // Queue only — Layout/App `ToasterContainer` is the sole renderer for dispatched toasts.
    setToasterMessages((prev) => [...prev, toast]);
    return id;
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasterMessages((prev) => {
      const updated = prev.filter((toast) => toast.id !== id);
      if (updated.length === 0) {
        setToasterMessage(null);
      }
      return updated;
    });
  }, []);

  /**
   * Legacy callers use `setToasterMessage({ message, type })`. Enqueue into the
   * global queue only (same as `addToast`) so Settings pages without a local
   * `<Toaster>` still show feedback without double-firing beside page-level toasters.
   */
  const setToasterMessageCompat = useCallback(
    (
      next:
        | ToastMessage
        | null
        | ((prev: ToastMessage | null) => ToastMessage | null)
    ) => {
      if (typeof next === 'function') {
        // Functional updates historically read `toasterMessage`; mirror last queue item.
        setToasterMessages((messages) => {
          const prev = messages.length > 0 ? messages[messages.length - 1] : null;
          const resolved = next(prev);
          if (resolved == null) {
            setToasterMessage(null);
            return [];
          }
          const id = Date.now() + Math.random();
          return [
            ...messages,
            {
              id,
              message: resolved.message,
              type: resolved.type,
              persistent: resolved.persistent || false,
              action: resolved.action || null,
              timestamp: Date.now(),
            },
          ];
        });
        return;
      }

      if (next == null) {
        setToasterMessage(null);
        setToasterMessages([]);
        return;
      }

      addToast(next);
    },
    [addToast]
  );

  const dispatch = useMemo<ToastDispatchValue>(
    () => ({
      setToasterMessage: setToasterMessageCompat,
      closeToaster,
      addToast,
      removeToast,
    }),
    [addToast, closeToaster, removeToast, setToasterMessageCompat]
  );

  const state = useMemo<ToastStateValue>(
    () => ({
      toasterMessage,
      toasterMessages,
    }),
    [toasterMessage, toasterMessages]
  );

  return (
    <ToastDispatchContext.Provider value={dispatch}>
      <ToastStateContext.Provider value={state}>{children}</ToastStateContext.Provider>
    </ToastDispatchContext.Provider>
  );
}

/**
 * Subscribe to toast state (ToasterContainer / legacy page-level Toaster).
 */
export function useToastState(): ToastStateValue {
  const ctx = useContext(ToastStateContext);
  if (!ctx) {
    throw new Error('useToastState must be used within UiShellProvider');
  }
  return ctx;
}

/**
 * Toast setters only — safe to call from DataProvider without toast re-renders.
 */
export function useToastDispatch(): ToastDispatchValue {
  const ctx = useContext(ToastDispatchContext);
  if (!ctx) {
    throw new Error('useToastDispatch must be used within UiShellProvider');
  }
  return ctx;
}
